/* import __COLOCATED_TEMPLATE__ from './signup-profile-form.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, getProperties } from '@ember/object';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency';
import { array, object, string, number } from 'yup';
import { validateSchema } from 'sh-www/utils/validators';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import {
  handleFormErrorOrModal,
} from 'sh-www/utils/handles-errors';
import clsx from 'clsx';

const SCHEMA = object({
  'place': object().required('You must select a location'),
  'services': array()
    .required('Please select at least one service')
    .min(1, 'Please select at least one service'),
  'min_rate': number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .required('Min rate must be a valid number')
    .min(10, 'Min rate must be at least 10')
    .test(
      'min-rate-less-than-max-rate-requirement',
      'Min rate must be less than or equal to max rate',
      (value, context) => value <= context.parent.max_rate
    ),
  'max_rate': number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .required('Max rate must be a valid number')
    .min(10, 'Max rate must be at least 10')
    .test(
      'max-rate-greater-than-min-rate-requirement',
      'Max rate must be greater than or equal to min rate',
      (value, context) => value >= context.parent.min_rate
    ),
  'phone': string()
    .required('Please enter your phone number')
    .min(7, 'Your phone number must be at least 7 digits'),
  'slug': string()
    .required('Please provide a display name')
    .min(5, 'Your display name must be at least 5 characters'),
  'photo': object().required('You must upload a profile photo'),
});

const FORM_INPUTS = ['place', 'services', 'min_rate', 'max_rate', 'phone', 'slug'];
const CLASS_NAME = 'GLM-freelancer-signup-form';

class SignupProfileForm extends Component {
  @service errorTracker;
  @service session;

  @tracked place = this.args.user.location;
  @tracked placeInputValue = this.args.user.location?.formattedAddress ?? '';

  @tracked services = this.args.user.services?.toArray() ?? [];
  @tracked min_rate = this.args.user.profile?.minRate ?? '';
  @tracked max_rate = this.args.user.profile?.maxRate ?? '';
  @tracked phone = this.args.user.phone ?? '';
  @tracked photo = this.storedProfileImage;
  @tracked slug = this.args.user.slug ?? '';
  @tracked errors = {};

  get classNames() {
    return clsx(
      this.args.class,
      CLASS_NAME
    );
  }

  get baseClassName() {
    return CLASS_NAME;
  }

  #sortedServicesCache = createCache(() => {
    const services = this.args.availableServices.slice();
    return services.sort((a, b) => {
      if (a.category === b.category) {
        return a.name.localeCompare(b.name);
      } else {
        return a.category.localeCompare(b.category);
      }
    });
  });

  get sortedServices() {
    return getValue(this.#sortedServicesCache);
  }

  get storedProfileImage() {
    const { image } = this.session.currentUser;

    return (image?.cloudId && !image.isPlaceholderImage)
      ? image
      : null;
  }

  get submitDisabled() {
    return this.submitTask.isRunning
      || Object.values(this.errors).some(error => !!error)
      || !this.photo
      || !this.place
      || !this.min_rate
      || !this.max_rate
      || !this.phone
      || !this.slug
      || this.services.length < 1;
  }

  @action
  validateForm(e) {
    e.preventDefault();
    const [_, validationErrors = {}] = validateSchema(getProperties(this, Object.keys(SCHEMA.fields)), SCHEMA);
    this.errors = validationErrors;

    if (Object.values(this.errors).every(error => !error)) {
      this.submitTask.perform();
    }
  }

  @action
  handlePlaceInput(value) {
    this.placeInputValue = value;
  }

  @action
  handleInput(field, value) {
    this[field] = value;

    if (field === 'max_rate' || field === 'min_rate') {
      this.errors = { ...this.errors, max_rate: undefined, min_rate: undefined };
      return;
    }

    if (this.errors[field]) {
      this.errors = { ...this.errors, [field]: undefined };
    }
  }

  @action
  handlePlaceChangeFromSuggestions(place) {
    this.place = place;
    this.placeInputValue = place?.formattedAddress ?? '';

    if (this.errors.place) {
      this.errors = { ...this.errors, place: undefined };
    }
  }

  @action
  handleServiceAdded(addedService) {
    this.services = [ ...this.services, addedService ]

    if (this.errors.services) {
      this.errors = { ...this.errors, services: undefined };
    }
  }

  @action
  handleServiceRemoved(removedService) {
    this.services = this.services.filter(service => service !== removedService);

    if (this.errors.services) {
      this.errors = { ...this.errors, services: undefined };
    }
  }

  @action
  handleImageUpload(image) {
    this.photo = image;

    if (this.errors.photo) {
      this.errors =  { ...this.errors, photo: undefined };
    }
  }

  @dropTask
  *submitTask() {
    try {
      yield this.args.updateUser(getProperties(this, [...FORM_INPUTS, 'photo']))
    } catch(error) {
      handleFormErrorOrModal(
        this,
        error,
        FORM_INPUTS,
        false,
        'There was an error creating your account. Please refresh and try again.',
        'signupProfileFail -- error updating user profile.' + JSON.stringify(error)
      );
    }
  }
}

export default SignupProfileForm;
